/**
 * Image Upload Service
 * Handles image uploads to backend API
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL!;

/**
 * Get Firebase ID token from current user
 */
const getAuthToken = async (): Promise<string> => {
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('No authenticated user found');
  return await auth.currentUser.getIdToken();
};

/**
 * Upload mobile image
 */
export const uploadMobileImage = async (
  storeId: string,
  file: File
): Promise<{ imageUrl: string }> => {
  try {
    const token = await getAuthToken();
    
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await fetch(
      `${API_BASE_URL}/api/deals/${storeId}/upload-mobile-image`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      }
    );

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || `Upload failed: ${response.statusText}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to upload mobile image');
    }

    return { imageUrl: result.data.imageUrl };
  } catch (error) {
    console.error('Error uploading mobile image:', error);
    throw error;
  }
};

/**
 * Upload desktop image
 */
export const uploadDesktopImage = async (
  storeId: string,
  file: File
): Promise<{ imageUrl: string }> => {
  try {
    const token = await getAuthToken();
    
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await fetch(
      `${API_BASE_URL}/api/deals/${storeId}/upload-desktop-image`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      }
    );

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || `Upload failed: ${response.statusText}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to upload desktop image');
    }

    return { imageUrl: result.data.imageUrl };
  } catch (error) {
    console.error('Error uploading desktop image:', error);
    throw error;
  }
};

/**
 * Delete all images for a store
 */
export const deleteImages = async (storeId: string): Promise<void> => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(
      `${API_BASE_URL}/api/deals/${storeId}/images`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || `Delete failed: ${response.statusText}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to delete images');
    }
  } catch (error) {
    console.error('Error deleting images:', error);
    throw error;
  }
};

/**
 * Validate image file (basic checks only - dimensions checked separately)
 */
export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  // Check file type
  if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
    return { valid: false, error: 'Please upload a JPG or PNG file' };
  }

  // Check file size (4MB limit)
  const maxSize = 4 * 1024 * 1024; // 4MB in bytes
  if (file.size > maxSize) {
    return { valid: false, error: 'File size must be less than 4MB' };
  }

  return { valid: true };
};

/**
 * Validate image dimensions with strict aspect ratio and resolution requirements
 */
export const validateImageDimensions = async (
  file: File,
  type: 'mobile' | 'desktop' | 'banner'
): Promise<{ valid: boolean; error?: string }> => {
  try {
    const dimensions = await getImageDimensions(file);
    const { width, height } = dimensions;
    const aspectRatio = width / height;

    if (type === 'banner') {
      // Banner requirements: 2.67:1 aspect ratio (24:9), based on 2400×900 reference
      const MIN_WIDTH = 1920;
      const MIN_HEIGHT = 720;
      const TARGET_RATIO = 2.67; // 24:9 ratio
      const RATIO_TOLERANCE = 0.15; // 15% tolerance

      // Check minimum resolution
      if (width < MIN_WIDTH || height < MIN_HEIGHT) {
        return {
          valid: false,
          error: `Banner image resolution too low. Your image is ${width}×${height}. Minimum required: ${MIN_WIDTH}×${MIN_HEIGHT} or larger with 24:9 ratio (2.67:1).`,
        };
      }

      // Check aspect ratio (allow tolerance)
      if (Math.abs(aspectRatio - TARGET_RATIO) > RATIO_TOLERANCE) {
        return {
          valid: false,
          error: `Banner image must have 24:9 aspect ratio (2.67:1). Your image is ${width}×${height} (${aspectRatio.toFixed(2)}:1). Please crop to 24:9 ratio (e.g., 2400×900, 1920×720).`,
        };
      }
    } else if (type === 'mobile') {
      // Mobile requirements: 2.22:1 aspect ratio (20:9), based on 2000×900 reference
      const MIN_WIDTH = 1600;
      const MIN_HEIGHT = 720;
      const TARGET_RATIO = 2.22; // 20:9 ratio
      const RATIO_TOLERANCE = 0.1; // 10% tolerance

      // Check minimum resolution
      if (width < MIN_WIDTH || height < MIN_HEIGHT) {
        return {
          valid: false,
          error: `Mobile image resolution too low. Your image is ${width}×${height}. Minimum required: ${MIN_WIDTH}×${MIN_HEIGHT} or larger with 20:9 ratio (2.22:1).`,
        };
      }

      // Check aspect ratio (allow tolerance)
      if (Math.abs(aspectRatio - TARGET_RATIO) > RATIO_TOLERANCE) {
        return {
          valid: false,
          error: `Mobile image must have 20:9 aspect ratio (2.22:1). Your image is ${width}×${height} (${aspectRatio.toFixed(2)}:1). Please crop to 20:9 ratio (e.g., 2000×900, 1600×720).`,
        };
      }
    } else {
      // Desktop requirements: 3.11:1 aspect ratio (28:9), based on 2800×900 reference
      const MIN_WIDTH = 2240;
      const MIN_HEIGHT = 720;
      const TARGET_RATIO = 3.11; // 28:9 ratio
      const RATIO_TOLERANCE = 0.15; // 15% tolerance

      // Check minimum resolution
      if (width < MIN_WIDTH || height < MIN_HEIGHT) {
        return {
          valid: false,
          error: `Desktop image resolution too low. Your image is ${width}×${height}. Minimum required: ${MIN_WIDTH}×${MIN_HEIGHT} or larger with 28:9 ratio (3.11:1).`,
        };
      }

      // Check aspect ratio (allow tolerance)
      if (Math.abs(aspectRatio - TARGET_RATIO) > RATIO_TOLERANCE) {
        return {
          valid: false,
          error: `Desktop image must have 28:9 aspect ratio (3.11:1). Your image is ${width}×${height} (${aspectRatio.toFixed(2)}:1). Please crop to 28:9 ratio (e.g., 2800×900, 2240×720).`,
        };
      }
    }

    return { valid: true };
  } catch (error) {
    console.error('Error validating image dimensions:', error);
    return { valid: false, error: 'Could not read image dimensions' };
  }
};

/**
 * Get image dimensions from file
 */
export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

